import { useParams } from "react-router-dom";
import { ActionButton, Dialog, DialogContent } from "@jhool-io/fe-components";
import { useState } from "react";
import { useGetDiagnosisCodesDisplay } from "../../../../hooks/helpers";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import {
    IAddendumDetails,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import { TERM_OF_CARE_NOTE_MED_LABELS } from "../../../../utils/constants";
import {
    formatDate,
    cn,
    displayNameInRightFormat,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import NoteContentItem from "../NoteContentItem/NoteContentItem";

import { IViewFile } from "../../../../utils/types";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import useGetDiagnosisCodeToDisplay from "../../../../modules/notes/hooks";
import styles from "../../../../modules/notes/components/NoteContent/NoteContent.module.scss";
import CheckIcon from "../../../../icons/Check";

interface ViewTermOfCareNoteMedProps {
    noteContent: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    fieldAddendums: {
        [key: string]: IAddendumDetails[];
    } | null;
}

export default function ViewTermOfCareNoteMed({
    noteContent,
    fieldAddendums,
}: ViewTermOfCareNoteMedProps) {
    const [docInView, setDocInView] = useState<IViewFile | null>(null);

    const diagonosisCodes = useGetDiagnosisCodesDisplay(
        noteContent.diagnosis_codes
    );

    const params = useParams();
    const noteId = params.noteId as string;
    const clientId = params.clientId as string;

    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    const displayDate = (value?: string) =>
        value ? formatDate(value, true) : "--";

    const displayCheckedOptions = (values?: string[]) =>
        Array.isArray(values) && values.length > 0 ? (
            <div className={styles.checkboxes}>
                {values.map((value) => (
                    <div className={styles.checkbox} key={value}>
                        <span
                            className={cn(styles.checkbox_wrap, styles.checked)}
                        >
                            <CheckIcon className="stroke-text" />
                        </span>
                        {value}
                    </div>
                ))}
            </div>
        ) : (
            "--"
        );

    const displayBooleanCheckbox = (label: string, isChecked?: boolean) => (
        <div className={styles.checkbox}>
            <span
                className={cn(styles.checkbox_wrap, {
                    [styles.checked]: Boolean(isChecked),
                })}
            >
                <CheckIcon className="stroke-text" />
            </span>
            {label}
        </div>
    );

    const displayEnumValue = (value?: string) =>
        value ? removeEnumUnderscore(value) : "--";

    return (
        <>
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.session_location}
                fieldId="session_location"
                addendums={fieldAddendums?.session_location}
                content={
                    noteContent.session_location
                        ? removeEnumUnderscore(noteContent.session_location)
                        : "--"
                }
            />
            {noteContent.session_location === SessionLocations.Telehealth && (
                <>
                    <NoteContentItem
                        headerText={
                            TERM_OF_CARE_NOTE_MED_LABELS.location_of_the_client
                        }
                        fieldId="location_of_the_client"
                        addendums={fieldAddendums?.location_of_the_client}
                        content={
                            noteContent.location_of_the_client
                                ? removeEnumUnderscore(
                                      noteContent.location_of_the_client
                                  )
                                : "--"
                        }
                    />
                    {noteContent.location_of_the_client === "other" && (
                        <NoteContentItem
                            headerText={
                                TERM_OF_CARE_NOTE_MED_LABELS.other_location_of_the_client
                            }
                            fieldId="other_location_of_the_client"
                            addendums={
                                fieldAddendums?.other_location_of_the_client
                            }
                            content={
                                noteContent.other_location_of_the_client || "--"
                            }
                        />
                    )}
                    <NoteContentItem
                        headerText={
                            TERM_OF_CARE_NOTE_MED_LABELS.location_of_the_provider
                        }
                        fieldId="location_of_the_provider"
                        addendums={fieldAddendums?.location_of_the_provider}
                        content={
                            noteContent.location_of_the_provider
                                ? removeEnumUnderscore(
                                      noteContent.location_of_the_provider
                                  )
                                : "--"
                        }
                    />
                    {noteContent.location_of_the_provider === "other" && (
                        <NoteContentItem
                            headerText={
                                TERM_OF_CARE_NOTE_MED_LABELS.other_location_of_the_provider
                            }
                            fieldId="other_location_of_the_provider"
                            addendums={
                                fieldAddendums?.other_location_of_the_provider
                            }
                            content={
                                noteContent.other_location_of_the_provider ||
                                "--"
                            }
                        />
                    )}
                    <NoteContentItem
                        headerText={
                            TERM_OF_CARE_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                        }
                        fieldId="consent_was_received_for_the_teletherapy_session"
                        addendums={
                            fieldAddendums?.consent_was_received_for_the_teletherapy_session
                        }
                        content={displayBooleanCheckbox(
                            TERM_OF_CARE_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session,
                            noteContent.consent_was_received_for_the_teletherapy_session
                        )}
                    />
                    <NoteContentItem
                        headerText={
                            TERM_OF_CARE_NOTE_MED_LABELS.the_session_was_conducted_via_video
                        }
                        fieldId="the_session_was_conducted_via_video"
                        addendums={
                            fieldAddendums?.the_session_was_conducted_via_video
                        }
                        content={displayBooleanCheckbox(
                            TERM_OF_CARE_NOTE_MED_LABELS.the_session_was_conducted_via_video,
                            noteContent.the_session_was_conducted_via_video
                        )}
                    />
                </>
            )}

            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.diagnosis_codes}
                fieldId="diagnosis_codes"
                addendums={fieldAddendums?.diagnosis_codes}
                content={
                    diagonosisCodes && Array.isArray(diagonosisCodes) ? (
                        <div className={styles.checkboxes}>
                            {diagonosisCodes.map((code) => (
                                <div className={styles.checkbox} key={code}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: true,
                                        })}
                                    >
                                        <CheckIcon className="stroke-text" />
                                    </span>
                                    {code}
                                </div>
                            ))}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.secondary_diagnosis}
                fieldId="secondary_diagnosis"
                addendums={fieldAddendums?.secondary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.secondary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.tertiary_diagnosis}
                fieldId="tertiary_diagnosis"
                addendums={fieldAddendums?.tertiary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.tertiary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={
                    TERM_OF_CARE_NOTE_MED_LABELS.additional_diagnosis_one
                }
                fieldId="additional_diagnosis_one"
                addendums={fieldAddendums?.additional_diagnosis_one}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_one
                )}
            />
            <NoteContentItem
                headerText={
                    TERM_OF_CARE_NOTE_MED_LABELS.additional_diagnosis_two
                }
                fieldId="additional_diagnosis_two"
                addendums={fieldAddendums?.additional_diagnosis_two}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_two
                )}
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.date_of_first_session}
                fieldId="date_of_first_session"
                addendums={fieldAddendums?.date_of_first_session}
                content={displayDate(noteContent.date_of_first_session)}
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.date_of_last_session}
                fieldId="date_of_last_session"
                addendums={fieldAddendums?.date_of_last_session}
                content={displayDate(noteContent.date_of_last_session)}
            />
            <NoteContentItem
                headerText={
                    TERM_OF_CARE_NOTE_MED_LABELS.total_number_of_therapy_sessions
                }
                fieldId="total_number_of_therapy_sessions"
                addendums={fieldAddendums?.total_number_of_therapy_sessions}
                content={noteContent.total_number_of_therapy_sessions || "--"}
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.presenting_concerns}
                fieldId="presenting_concerns"
                addendums={fieldAddendums?.presenting_concerns}
                content={noteContent.presenting_concerns || "--"}
            />
            <NoteContentItem
                headerText={
                    TERM_OF_CARE_NOTE_MED_LABELS.planned_treatment_and_goals
                }
                fieldId="planned_treatment_and_goals"
                addendums={fieldAddendums?.planned_treatment_and_goals}
                content={noteContent.planned_treatment_and_goals || "--"}
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.course_of_treatment}
                fieldId="course_of_treatment"
                addendums={fieldAddendums?.course_of_treatment}
                content={noteContent.course_of_treatment || "--"}
            />
            <NoteContentItem
                headerText={
                    TERM_OF_CARE_NOTE_MED_LABELS.patient_final_condition
                }
                fieldId="patient_final_condition"
                addendums={fieldAddendums?.patient_final_condition}
                content={displayCheckedOptions(
                    noteContent.patient_final_condition
                )}
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.prognosis}
                fieldId="prognosis"
                addendums={fieldAddendums?.prognosis}
                content={displayCheckedOptions(noteContent.prognosis)}
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.reason_for_termination}
                fieldId="reason_for_termination"
                addendums={fieldAddendums?.reason_for_termination}
                content={noteContent.reason_for_termination || "--"}
            />
            <NoteContentItem
                headerText={
                    TERM_OF_CARE_NOTE_MED_LABELS.discharge_plan_and_follow_up
                }
                fieldId="discharge_plan_and_follow_up"
                addendums={fieldAddendums?.discharge_plan_and_follow_up}
                content={noteContent.discharge_plan_and_follow_up || "--"}
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.condition_type}
                fieldId="condition_type"
                addendums={fieldAddendums?.condition_type}
                content={displayEnumValue(noteContent.condition_type)}
            />
            <NoteContentItem
                headerText={
                    TERM_OF_CARE_NOTE_MED_LABELS.condition_type_additional_details
                }
                fieldId="condition_type_additional_details"
                addendums={fieldAddendums?.condition_type_additional_details}
                content={noteContent.condition_type_additional_details || "--"}
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.condition_status}
                fieldId="condition_status"
                addendums={fieldAddendums?.condition_status}
                content={displayEnumValue(noteContent.condition_status)}
            />
            <NoteContentItem
                headerText={
                    TERM_OF_CARE_NOTE_MED_LABELS.condition_status_additional_details
                }
                fieldId="condition_status_additional_details"
                addendums={fieldAddendums?.condition_status_additional_details}
                content={
                    noteContent.condition_status_additional_details || "--"
                }
            />
            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.modality}
                fieldId="modality"
                addendums={fieldAddendums?.modality}
                content={displayEnumValue(noteContent.modality)}
            />

            <NoteContentItem
                headerText={TERM_OF_CARE_NOTE_MED_LABELS.files}
                fieldId="files"
                addendums={fieldAddendums?.files}
                content={
                    <div>
                        {data?.data?.note_content_documents &&
                        data?.data?.note_content_documents.length ? (
                            <div className="flex gap-[var(--spacing-items)] flex-wrap">
                                {data?.data?.note_content_documents.map(
                                    (doc) => (
                                        <ActionButton
                                            key={doc.note_content_document_id}
                                            variant="can-hover"
                                            size="auto"
                                            className="h-7 !text-text text-caption underline"
                                            actionTitle="View file"
                                            onClick={() => {
                                                setDocInView(doc);
                                            }}
                                        >
                                            <span>{`${displayNameInRightFormat(
                                                {
                                                    firstName:
                                                        doc.uploaded_by
                                                            .first_name,
                                                    lastName:
                                                        doc.uploaded_by
                                                            .last_name,
                                                },
                                                false
                                            )} -  ${doc.document_name}`}</span>
                                        </ActionButton>
                                    )
                                )}
                            </div>
                        ) : (
                            "--"
                        )}
                    </div>
                }
            />
        </>
    );
}
